import { useCallback, useEffect, useRef, useState } from "react";
import { useImageSize } from "react-image-size";
import { Link } from "react-router";
import PulseShareToolPopover from "@features/pulse/components/PulseShareToolPopover";
import { useGetShareCount } from "@features/pulse/hooks/useGetShareCount";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { SanitizedPulseData } from "@features/pulse/utils/types";
import { cn, getFormattedUrlPath } from "@util/helpers";

type PulseCardProps = {
  /** Article data to be displayed in the card */
  article: SanitizedPulseData;
  /** Indicates if the card is currently active */
  isActive: boolean;
  /** Indicates if the card is the previous one in the carousel */
  isPrevious: boolean;
  /** Currently active article index */
  activeArticle?: number;
  /** Callback to handle enabling autoplay */
  handleSetIsAutoplayPaused: (isPaused: boolean) => void;
};

/** Desktop Individual pulse card */
const PulseCard = ({
  article,
  isActive,
  isPrevious,
  handleSetIsAutoplayPaused,
  activeArticle,
}: PulseCardProps): React.ReactElement => {
  const { shareCount, incrementShareCount } = useGetShareCount(article.id);
  const [isPortrait, setIsPortrait] = useState<boolean>(false);
  const [dimensions] = useImageSize(article.media || "");
  const { sendCustomEvent } = useSendPulseGaData();
  const contentRef = useRef<HTMLUListElement>(null);

  const handleIncrementShareCount = () => {
    incrementShareCount();
  };

  useEffect(() => {
    if (dimensions) {
      setIsPortrait(dimensions.height > dimensions.width);
    }
  }, [dimensions]);

  const fullArticleUrl = getFormattedUrlPath(article.url, [
    { key: "ref", value: "pulse" },
  ]);

  const handleFullStoryClick = () => {
    sendCustomEvent(
      `desktop::click full story::${activeArticle}`,
      article.title
    );
  };

  const scrollTimeout = useRef<NodeJS.Timeout>();

  const handleScrollEnd = useCallback(() => {
    sendCustomEvent(`desktop::scroll::${activeArticle}>`, article.title);
  }, [sendCustomEvent, activeArticle, article.title]);

  const handleScroll = useCallback(() => {
    clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      handleScrollEnd();
    }, 200); // Adjust debounce time as needed
  }, [handleScrollEnd]);

  useEffect(() => {
    const contentElement = contentRef.current;

    if (!contentElement) return;

    contentElement.addEventListener("scroll", handleScroll);

    return () => {
      contentElement.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout.current);
    };
  }, [contentRef, handleScroll]);

  return (
    <div
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-[8px] border border-white/10 bg-[#1A1A1A]"
      )}
      data-testid={"pulse-individual-article-card-component"}
    >
      <div className="relative min-h-fit [@media(min-height:750px)]:max-h-64 [@media(min-height:750px)]:min-h-64 [@media(min-height:750px)]:overflow-hidden">
        {isPortrait ? (
          <div
            className="absolute z-0 hidden h-full w-full bg-cover bg-center bg-no-repeat blur-[20px] [@media(min-height:750px)]:block"
            style={{
              backgroundImage: `url(${article.media})`,
            }}
          />
        ) : null}

        <div
          className={cn(
            "absolute z-0 hidden h-full w-full bg-cover bg-center bg-no-repeat [@media(min-height:750px)]:block",
            {
              "bg-contain": isPortrait,
            }
          )}
          style={{
            backgroundImage: `url(${article.media})`,
          }}
        />

        <div className="relative z-10 flex h-full flex-col justify-end from-black/80 via-black/50 to-transparent px-6 py-3 [@media(min-height:750px)]:bg-gradient-to-t">
          {article.sections ? (
            <p className="mb-0 pb-2 font-poppins text-4xs font-medium tracking-widest text-white">
              {article.sections?.toUpperCase()}
            </p>
          ) : null}

          <p
            className={cn(
              "line-clamp-4 font-lct text-4xl font-semibold leading-[1.3] -tracking-2% text-white",
              { "text-lg": !isActive && !isPrevious }
            )}
          >
            {article.title}
          </p>
        </div>
      </div>

      {article.summaries.length > 0 ? (
        <ul
          className={cn(
            "relative z-0 ml-4 mt-1 flex-grow list-outside list-disc overflow-scroll p-4 pb-15 pt-0 [@media(min-height:750px)]:mt-4",
            "scrollbar"
          )}
          aria-label={`pulse-article-summaries-${article.id}`}
          ref={contentRef}
        >
          {article.summaries.map((summary, index) => (
            <li
              key={index}
              className="mt-2 font-lucida text-base font-normal -tracking-5% text-white"
            >
              {summary}
            </li>
          ))}
        </ul>
      ) : null}

      <div
        className={cn(
          "relative z-10 flex justify-between border-t border-white/10 text-white",
          "after:absolute after:-top-[calc(4rem_+_1px)] after:h-16 after:w-full after:bg-gradient-to-t after:from-[#1A1A1A] after:from-30% after:to-90%",
          { "pointer-events-none": !isActive }
        )}
      >
        <PulseShareToolPopover
          articleTitle={article.title}
          shareUrl={article.shareUrl || ""}
          text="Share"
          className="w-full border-r border-white/10 px-15 py-4 font-poppins text-xs font-semibold tracking-2% text-white hover:bg-[#363636]"
          shareCount={shareCount}
          callback={handleIncrementShareCount}
          handleSetIsAutoplayPaused={(isPaused) => {
            handleSetIsAutoplayPaused(isPaused);
          }}
          articleId={article.id}
          activeArticle={activeArticle}
        />

        <Link
          to={fullArticleUrl}
          target="_blank"
          className="flex w-1/2 items-center justify-center px-15 py-4 text-center font-poppins text-xs font-semibold tracking-2% text-white hover:bg-[#363636]"
          onClick={handleFullStoryClick}
        >
          Full story
        </Link>
      </div>
    </div>
  );
};

export default PulseCard;
